import React, { useState } from 'react';
import {
  Box, Stack, Typography, Avatar, IconButton, Badge, Tooltip,
  Popover, List, ListItem, ListItemText, Button, Drawer,
} from '@mui/material';
import NotificationsRoundedIcon from '@mui/icons-material/NotificationsRounded';
import LogoutRoundedIcon        from '@mui/icons-material/LogoutRounded';
import MenuRoundedIcon          from '@mui/icons-material/MenuRounded';
import SpaRoundedIcon           from '@mui/icons-material/SpaRounded';
import { useShell }     from '../shell';
import { useV4Variant } from '../theme';
import GrowSetupPicker  from './GrowSetupPicker';
import ColorModeToggle  from './ColorModeToggle';
import ThemePicker      from './ThemePicker';

export default function NavBar() {
  const shell   = useShell();
  const variant = useV4Variant();
  const { changes, saveConfig, avatar, username } = shell;
  const [anchorEl,   setAnchor]  = useState(null);
  const [drawerOpen, setDrawer]  = useState(false);

  const hasUnsaved = Object.keys(changes).length > 0;

  const handleLogout = shell.logout;

  const NavLinks = ({ vertical = false }) => (
    <Stack direction={vertical ? 'column' : 'row'} spacing={vertical ? 0.5 : 0}>
      {shell.items.map(({ label, icon: Icon, path }) => {
        const active = shell.isActive(path);
        return (
          <Box
            key={path}
            onClick={() => { shell.go(path); if (vertical) setDrawer(false); }}
            sx={{
              display: 'flex', alignItems: 'center',
              gap: vertical ? 1.5 : 0,
              flexDirection: vertical ? 'row' : 'column',
              px: vertical ? 2 : 1.5, py: vertical ? 1.25 : 0.75,
              borderRadius: '12px',
              cursor: 'pointer',
              position: 'relative',
              color: active ? 'var(--v4-cyan)' : 'rgba(var(--v4-muted-rgb),0.5)',
              background: active ? 'rgba(var(--v4-cyan-rgb),0.1)' : 'transparent',
              transition: 'all 0.18s ease',
              '&:hover': { color: 'var(--v4-cyan)', background: 'rgba(var(--v4-cyan-rgb),0.07)' },
            }}
          >
            <Icon sx={{ fontSize: vertical ? 20 : 18, color: 'inherit', filter: active ? 'drop-shadow(0 0 5px var(--v4-cyan))' : 'none' }} />
            <Typography sx={{
              fontSize: vertical ? '0.85rem' : '0.62rem',
              fontWeight: active ? 700 : 500,
              color: 'inherit',
              whiteSpace: 'nowrap',
            }}>
              {label}
            </Typography>
            {active && !vertical && (
              <Box sx={{
                position: 'absolute', bottom: -1, left: '20%', right: '20%',
                height: 2, borderRadius: 1,
                background: 'linear-gradient(90deg,transparent,var(--v4-cyan),transparent)',
              }} />
            )}
          </Box>
        );
      })}
    </Stack>
  );

  return (
    <>
      <Box
        component="nav"
        sx={{
          height: 60,
          display: 'flex', alignItems: 'center',
          px: { xs: 2, md: 3 },
          gap: 2,
          background: 'rgba(var(--v4-surface2-rgb),0.92)',
          borderBottom: '1px solid rgba(var(--v4-cyan-rgb),0.08)',
          backdropFilter: 'blur(20px)',
          position: 'sticky', top: 0, zIndex: 200,
          flexShrink: 0,
        }}
      >
        {/* Logo */}
        <Stack direction="row" alignItems="center" spacing={1.25} sx={{ minWidth: 120 }}>
          <Box sx={{
            width: 32, height: 32, borderRadius: '10px',
            background: 'linear-gradient(135deg,var(--v4-cyan) 0%,var(--v4-cyan-deep) 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 16px rgba(var(--v4-cyan-rgb),0.4)',
            flexShrink: 0,
          }}>
            <SpaRoundedIcon sx={{ color: '#fff', fontSize: 17 }} />
          </Box>
          <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
            <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', color: 'var(--v4-cyan)', lineHeight: 1 }}>{variant.brand}</Typography>
            <Typography sx={{ fontSize: '0.58rem', color: 'rgba(var(--v4-cyan-rgb),0.4)', letterSpacing: '0.12em', textTransform: 'uppercase' }}>{variant.tag}</Typography>
          </Box>
        </Stack>

        {/* Grow Setup Picker */}
        <GrowSetupPicker />

        {/* Nav links — desktop */}
        <Box sx={{ flex: 1, display: { xs: 'none', md: 'flex' }, justifyContent: 'center' }}>
          <NavLinks />
        </Box>

        {/* Right actions */}
        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ ml: 'auto' }}>
          <ThemePicker />
          <ColorModeToggle />

          <Tooltip title={hasUnsaved ? 'Ungespeicherte Änderungen' : 'Keine Änderungen'} placement="bottom">
            <IconButton
              size="small"
              onClick={e => setAnchor(e.currentTarget)}
              sx={{
                color: hasUnsaved ? 'var(--v4-orange)' : 'rgba(var(--v4-muted-rgb),0.4)',
                background: hasUnsaved ? 'rgba(var(--v4-orange-rgb),0.1)' : 'transparent',
                border: `1px solid ${hasUnsaved ? 'rgba(var(--v4-orange-rgb),0.3)' : 'transparent'}`,
                borderRadius: '10px',
              }}
            >
              <Badge badgeContent={hasUnsaved ? Object.keys(changes).length : 0} color="warning">
                <NotificationsRoundedIcon fontSize="small" />
              </Badge>
            </IconButton>
          </Tooltip>

          <Tooltip title={username || 'Profil'} placement="bottom">
            <Avatar
              src={avatar}
              sx={{ width: 32, height: 32, cursor: 'pointer', border: '2px solid rgba(var(--v4-cyan-rgb),0.25)', '&:hover': { border: '2px solid var(--v4-cyan)' }, transition: 'border 0.18s' }}
              onClick={() => shell.go('/settings')}
            />
          </Tooltip>

          <Tooltip title="Logout" placement="bottom">
            <IconButton size="small" onClick={handleLogout} sx={{ color: 'rgba(var(--v4-muted-rgb),0.35)', '&:hover': { color: 'var(--v4-red)' }, borderRadius: '10px' }}>
              <LogoutRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* Hamburger mobile */}
          <IconButton size="small" onClick={() => setDrawer(true)} sx={{ display: { xs: 'flex', md: 'none' }, color: 'rgba(var(--v4-muted-rgb),0.6)' }}>
            <MenuRoundedIcon />
          </IconButton>
        </Stack>
      </Box>

      {/* Notifications popover */}
      <Popover
        open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { background: 'rgba(var(--v4-surface2-rgb),0.97)', border: '1px solid rgba(var(--v4-cyan-rgb),0.15)', borderRadius: '14px', backdropFilter: 'blur(20px)', mt: 0.5, width: 280 } }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="subtitle2" sx={{ color: 'var(--v4-cyan)', fontWeight: 700, mb: 1 }}>Ungespeichert</Typography>
          <List dense disablePadding>
            {hasUnsaved ? Object.entries(changes).map(([k, v]) => (
              <ListItem key={k} disablePadding sx={{ py: 0.25 }}>
                <ListItemText primary={`${k} → ${v}`} primaryTypographyProps={{ variant: 'caption', color: 'rgba(var(--v4-text-rgb),0.7)' }} />
              </ListItem>
            )) : (
              <ListItem disablePadding>
                <ListItemText primary="Keine Änderungen" primaryTypographyProps={{ variant: 'caption', color: 'rgba(var(--v4-muted-rgb),0.4)' }} />
              </ListItem>
            )}
          </List>
          {hasUnsaved && <Button fullWidth variant="contained" sx={{ mt: 1.5 }} onClick={() => { saveConfig(); setAnchor(null); }}>Speichern</Button>}
        </Box>
      </Popover>

      {/* Mobile drawer */}
      <Drawer
        anchor="left" open={drawerOpen} onClose={() => setDrawer(false)}
        PaperProps={{ sx: { background: 'rgba(var(--v4-surface2-rgb),0.97)', border: 'none', backdropFilter: 'blur(24px)', width: 250, pt: 2, px: 1 } }}
      >
        <Stack direction="row" alignItems="center" spacing={1.25} sx={{ px: 1.5, mb: 2 }}>
          <SpaRoundedIcon sx={{ color: 'var(--v4-cyan)', fontSize: 22 }} />
          <Typography sx={{ fontWeight: 800, color: 'var(--v4-cyan)' }}>{variant.brand}</Typography>
        </Stack>
        {/* Setup picker inside drawer */}
        <Box sx={{ px: 1, mb: 2 }}>
          <GrowSetupPicker />
        </Box>
        <Box sx={{ height: '1px', background: 'rgba(var(--v4-cyan-rgb),0.07)', mb: 1.5 }} />
        <NavLinks vertical />
      </Drawer>
    </>
  );
}
